import { SidebarIcon } from "@phosphor-icons/react/dist/csr/Sidebar";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { useNavColumn } from "./nav-column-state";
import { railHasAttention, type RailEntry } from "./rail-model";
import { SIDEBAR_ICON_CURRENT } from "./sidebar-row";

export const ColumnReveal = ({
  label,
  open,
  onShow,
}: {
  label: string;
  open: boolean;
  onShow: () => void;
}) => (
  <Stack
    inert={open ? undefined : true}
    className={cn("nav-reveal w-full", { "nav-reveal-open": open })}
  >
    <Stack align="center" className="nav-reveal-clip w-full">
      <Stack gap={2} align="center" className="nav-reveal-control w-full pb-2">
        <Tooltip disabled={!open}>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Show ${label}`}
                aria-controls="nav-panel"
                aria-expanded={false}
                onClick={onShow}
                className="transition-[background-color,color] duration-150 ease-[var(--sidebar-ease-out)]"
              />
            }
          >
            <SidebarIcon />
          </TooltipTrigger>
          <TooltipContent side="right">Show {label}</TooltipContent>
        </Tooltip>
        <span aria-hidden className="h-px w-4 shrink-0 bg-line" />
      </Stack>
    </Stack>
  </Stack>
);

const railName = (entry: RailEntry, unseen: boolean, attention: boolean) =>
  [entry.label, unseen ? "New" : null, attention ? "Needs attention" : null]
    .filter(Boolean)
    .join(", ");

const controlClass = (active: boolean) =>
  cn(
    "relative grid size-8 place-items-center rounded-lg text-ink outline-hidden transition-[background-color,color] duration-150 ease-[var(--sidebar-ease-out)] hover:bg-neutral-subtle-hovered focus-visible:ring-2 focus-visible:ring-focus motion-safe:active:translate-y-px",
    { "bg-neutral text-accent-orange": active },
  );

// A rail entry is a link when it has no column, and a button that opens its
// column otherwise. Its state comes from the nav column, so the rail only
// lists entries.
export const RailControl = ({ entry }: { entry: RailEntry }) => {
  const column = useNavColumn();
  const unseen = entry.sourceHrefs.some((href) => column.unseenHrefs.has(href));
  const hasColumn = entry.sections.length > 0;
  const expanded = entry.isActive && !column.hidden;
  const preview = hasColumn && (!column.active || column.hidden);
  const onClick = () => column.select(entry);
  const attention = railHasAttention(entry);
  const icon = (
    <span className="relative grid size-4 place-items-center">
      <entry.icon
        weight={entry.isActive ? "duotone" : "regular"}
        className={cn("size-4", { [SIDEBAR_ICON_CURRENT]: entry.isActive })}
      />
      {attention ? (
        <WarningIcon
          weight="fill"
          aria-hidden
          className="absolute -end-1 -top-1 size-3 text-warning"
        />
      ) : (
        unseen && (
          <span
            aria-hidden
            className="absolute -end-0.5 -top-0.5 size-1.5 rounded-full bg-accent-orange ring-2 ring-surface-sunken"
          />
        )
      )}
    </span>
  );
  if (preview) {
    return (
      <button
        type="button"
        aria-label={railName(entry, unseen, attention)}
        aria-expanded={expanded}
        aria-controls="nav-panel"
        onClick={onClick}
        onMouseMove={() => column.preview.show(entry)}
        onMouseLeave={column.preview.hideSoon}
        className={controlClass(entry.isActive)}
      >
        {icon}
      </button>
    );
  }
  const trigger = !hasColumn ? (
    <Link
      to={entry.to}
      aria-current={entry.isActive ? "page" : undefined}
      aria-label={railName(entry, unseen, attention)}
      className={controlClass(entry.isActive)}
    />
  ) : (
    <button
      type="button"
      aria-label={railName(entry, unseen, attention)}
      aria-expanded={expanded}
      aria-controls="nav-panel"
      onClick={onClick}
      className={controlClass(entry.isActive)}
    />
  );
  return (
    <Tooltip>
      <TooltipTrigger render={trigger}>{icon}</TooltipTrigger>
      <TooltipContent side="right">{entry.label}</TooltipContent>
    </Tooltip>
  );
};
